import type { HTMLAttributes, KeyboardEventHandler } from 'react';
import React, { forwardRef } from 'react';

import DeleteHistoricalSearchButton from '@/core/components/DeleteHistoricalSearchButton';
import { InnerInput } from '@/core/components/inputs/TextInput/InnerInput';
import OptionToDisplay from '@/core/components/OptionToDisplay';
import useTextInput from '@/core/hooks/inputs/useTextInput';
import { SelectOption, useAutoCompleteInput } from '@/core/hooks/inputs/useTextInput/useAutoCompleteInput';
import { useTranslationContext } from '@/core/lib/translation/translation.context';
import type { NewInputProps } from '@/core/types/components';

interface AutoCompleteInputProps extends NewInputProps<string> {
  type?: 'text' | 'email';
  autoComplete?: 'name' | 'email' | 'username' | 'address-level2' | 'off';
  inputMode?: HTMLAttributes<HTMLInputElement>['inputMode'];
  noCheck?: boolean;
  selectOptions?: SelectOption[];
  handleOnKeyDown?: KeyboardEventHandler;
  hideCleanIcon?: boolean;
}

export const AutoCompleteInput = forwardRef<HTMLInputElement, AutoCompleteInputProps>((props, forwardedRef) => {
  const { t } = useTranslationContext(['common']);

  const {
    props: { type, autoComplete, inputMode, enterKeyHint, selectOptions, handleOnKeyDown },
    cleanAttrs,
    onClick,
    ...attrs
  } = useTextInput<AutoCompleteInputProps>({ forwardedRef, ...props });
  const { isOpen, onOpen, containerRef, selectOptionsFiltered, onClose } = useAutoCompleteInput({
    selectOptions: selectOptions,
    searchValue: attrs.value,
  });

  const optionsToDisplay = attrs.value ? selectOptionsFiltered : selectOptions;

  return (
    <div className="relative w-full">
      <InnerInput
        {...attrs}
        onClick={e => {
          onClick?.(e);
          onOpen();
        }}
        type={type}
        autoComplete={autoComplete}
        inputMode={inputMode}
        enterKeyHint={enterKeyHint}
        onKeyDown={handleOnKeyDown}
        noCheck={props.noCheck}
        cleanAttrs={{
          ...cleanAttrs,
          onClick: () => {
            cleanAttrs.onClick();
            onOpen();
          },
        }}
      />

      {optionsToDisplay?.length && optionsToDisplay.length > 0 && isOpen ? (
        <div ref={containerRef} className="absolute top-12 z-20 flex h-auto max-h-select w-full min-w-67 flex-col overflow-auto rounded-2 bg-bg-primary p-3 pb-0 shadow-md">
          <div className="mt-4 flex flex-col border-b border-stroke-tertiary">
            <p className="text-body-secondary font-semibold text-content-primary">{t('inputs.text.historical', { ns: 'common' })}</p>
            <div className="my-2 flex flex-col">
              {optionsToDisplay.slice(0, 10).map(({ hash, title }) => (
                <OptionToDisplay
                  key={hash}
                  title={title}
                  onSelectHistoricalSearch={event => {
                    event.stopPropagation();
                    event.preventDefault();
                    attrs.onChange({ target: { value: title } } as React.ChangeEvent<HTMLInputElement>);
                    onClose();
                  }}
                >
                  <DeleteHistoricalSearchButton hash={hash} title={title} />
                </OptionToDisplay>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
});
